import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "../components/app/Header";
import { BackButton } from "../components/app/BackButton";
import { Leaderboard } from "../components/app/Leaderboard";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { toast } from "sonner";
import {
  ArrowUpRight,
  ArrowDownRight,
  MinusSquare,
  Heart,
  Loader2,
  Users,
  ArrowRight,
  Flame,
  MessageCircle,
} from "lucide-react";

const PAGE_SIZE = 12;

const DirectionDot = ({ d }) => {
  if (d === "BULLISH") return <ArrowUpRight className="h-3.5 w-3.5 text-bull" strokeWidth={2} />;
  if (d === "BEARISH") return <ArrowDownRight className="h-3.5 w-3.5 text-bear" strokeWidth={2} />;
  return <MinusSquare className="h-3.5 w-3.5 text-neutral-400" strokeWidth={2} />;
};

const dirColor = (d) =>
  d === "BULLISH" ? "text-bull" : d === "BEARISH" ? "text-bear" : "text-neutral-400";

const formatAgo = (iso) => {
  try {
    const d = new Date(iso);
    const diff = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diff < 60) return `${diff}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  } catch {
    return "";
  }
};

export default function Community() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [direction, setDirection] = useState("ALL");
  const [market, setMarket] = useState("ALL");
  const [sortMode, setSortMode] = useState("recent");
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const load = useCallback(
    async (reset = false) => {
      const currentSkip = reset ? 0 : skip;
      if (reset) setLoading(true);
      else setLoadingMore(true);
      try {
        const params = { limit: PAGE_SIZE, skip: currentSkip, sort: sortMode };
        if (direction !== "ALL") params.direction = direction;
        if (market !== "ALL") params.market = market;
        const { data } = await api.get("/community", { params });
        setItems((prev) => (reset ? data : [...prev, ...data]));
        setHasMore(data.length === PAGE_SIZE);
        if (reset) setSkip(data.length);
        else setSkip(currentSkip + data.length);
      } catch {
        toast.error("Failed to load community feed");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [direction, market, sortMode, skip]
  );

  // Reload when filters change
  useEffect(() => {
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direction, market, sortMode]);

  const toggleLike = async (item) => {
    if (!user) {
      toast(t("community_login_to_like"));
      navigate("/login");
      return;
    }
    // Optimistic update
    setItems((prev) =>
      prev.map((it) =>
        it.analysis_id === item.analysis_id
          ? {
              ...it,
              liked_by_me: !it.liked_by_me,
              share_likes: it.share_likes + (it.liked_by_me ? -1 : 1),
            }
          : it
      )
    );
    try {
      await api.post(`/community/${item.analysis_id}/like`);
    } catch {
      // revert
      setItems((prev) =>
        prev.map((it) =>
          it.analysis_id === item.analysis_id
            ? {
                ...it,
                liked_by_me: !it.liked_by_me,
                share_likes: it.share_likes + (it.liked_by_me ? -1 : 1),
              }
            : it
        )
      );
      toast.error("Failed to update like");
    }
  };

  return (
    <div className="min-h-screen" data-testid="community-page">
      <Header />
      <main className="mx-auto max-w-6xl px-6 md:px-8 py-8 space-y-6">
        <BackButton fallback={user ? "/dashboard" : "/"} />

        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <span className="label-mono inline-flex items-center gap-2">
              <Users className="h-3 w-3" strokeWidth={2} /> COMMUNITY //
            </span>
            <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tighter">
              {t("community_title")}
              <span className="text-bull">_</span>
            </h1>
            <p className="mt-2 text-sm text-neutral-400">{t("community_subtitle")}</p>
          </div>
        </div>

        {/* Sort tabs */}
        <Tabs value={sortMode} onValueChange={setSortMode} data-testid="community-sort-tabs">
          <TabsList className="bg-[#0e0e0e] border border-white/10 rounded-none h-9 p-0 inline-flex">
            <TabsTrigger value="recent" className="rounded-none font-mono text-xs px-4 data-[state=active]:bg-bull data-[state=active]:text-black" data-testid="sort-recent">
              {t("sort_recent")}
            </TabsTrigger>
            <TabsTrigger value="trending" className="rounded-none font-mono text-xs px-4 data-[state=active]:bg-bull data-[state=active]:text-black" data-testid="sort-trending">
              <Flame className="h-3 w-3 mr-1" strokeWidth={2} />{t("sort_trending")}
            </TabsTrigger>
            <TabsTrigger value="top" className="rounded-none font-mono text-xs px-4 data-[state=active]:bg-bull data-[state=active]:text-black" data-testid="sort-top">
              {t("sort_top")}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Main feed column */}
          <div className="lg:col-span-8 space-y-6">

            {/* Filters */}
            <div className="flex flex-wrap gap-3" data-testid="community-filters">
              <div className="flex items-center gap-2">
                <span className="label-mono">{t("community_filter_direction")}:</span>
                <Select value={direction} onValueChange={setDirection}>
                  <SelectTrigger
                    className="bg-[#0e0e0e] border-white/10 rounded-none h-9 w-36 font-mono text-xs"
                    data-testid="filter-direction"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-white/10 rounded-none">
                    <SelectItem value="ALL">{t("community_filter_all")}</SelectItem>
                    <SelectItem value="BULLISH">{t("direction_BULLISH")}</SelectItem>
                    <SelectItem value="BEARISH">{t("direction_BEARISH")}</SelectItem>
                    <SelectItem value="NEUTRAL">{t("direction_NEUTRAL")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="label-mono">{t("community_filter_market")}:</span>
                <Select value={market} onValueChange={setMarket}>
                  <SelectTrigger
                    className="bg-[#0e0e0e] border-white/10 rounded-none h-9 w-36 font-mono text-xs"
                    data-testid="filter-market"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-white/10 rounded-none">
                    <SelectItem value="ALL">{t("community_filter_all")}</SelectItem>
                    <SelectItem value="CRYPTO">{t("market_CRYPTO")}</SelectItem>
                    <SelectItem value="FOREX">{t("market_FOREX")}</SelectItem>
                    <SelectItem value="EQUITY">{t("market_EQUITY")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

        {/* Feed grid */}
        {loading ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-bull" />
          </div>
        ) : items.length === 0 ? (
          <div className="surface-card p-12 text-center" data-testid="community-empty">
            <Users className="h-7 w-7 mx-auto text-neutral-600" strokeWidth={1.5} />
            <p className="mt-3 text-sm text-neutral-500">{t("community_empty")}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="community-grid">
              {items.map((it) => (
                <article
                  key={it.analysis_id}
                  className="surface-card overflow-hidden group transition-all hover:border-white/20"
                  data-testid={`community-card-${it.analysis_id}`}
                >
                  {it.thumbnail && (
                    <button
                      onClick={() => navigate(`/community/${it.analysis_id}`)}
                      className="block w-full aspect-[16/9] bg-black border-b border-white/10 overflow-hidden"
                      aria-label="View analysis"
                    >
                      <img
                        src={it.thumbnail}
                        alt="chart"
                        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                      />
                    </button>
                  )}
                  <div className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center gap-2 font-mono text-xs">
                        <DirectionDot d={it.direction} />
                        <span className={dirColor(it.direction)}>{t(`direction_${it.direction}`)}</span>
                        <span className="text-neutral-500">·</span>
                        <span className="text-neutral-400">{it.confidence}%</span>
                      </span>
                      <div className="flex items-center gap-2">
                        {it.is_trending && (
                          <span
                            className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.18em] px-1.5 py-0.5 border border-warn/40 text-warn bg-warn/[0.06]"
                            data-testid={`trending-badge-${it.analysis_id}`}
                            title="Trending — +10 likes in 24h"
                          >
                            <Flame className="h-2.5 w-2.5" strokeWidth={2.5} />
                            {t("trending_badge")}
                          </span>
                        )}
                        <span className="font-mono text-[10px] text-neutral-500">
                          {formatAgo(it.shared_at)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2 font-mono text-sm">
                      <span className="text-white truncate">
                        {it.symbol || "—"}{" "}
                        {it.timeframe && (
                          <span className="text-neutral-500 text-xs">· {it.timeframe}</span>
                        )}
                      </span>
                      <span className="font-mono text-[10px] tracking-[0.18em] px-2 py-0.5 border border-white/15 text-neutral-300">
                        {it.market_type}
                      </span>
                    </div>
                    {it.analysis_summary && (
                      <p className="text-xs text-neutral-400 line-clamp-2 leading-relaxed">
                        {it.analysis_summary}
                      </p>
                    )}
                    <div className="flex items-center justify-between pt-2 border-t border-white/5">
                      <div className="flex items-center gap-2 font-mono text-[11px] text-neutral-400">
                        <span className="h-6 w-6 inline-flex items-center justify-center border border-white/10 bg-white/[0.04] text-bull">
                          {it.author_initial}
                        </span>
                        <span className="truncate max-w-[100px]">{it.author_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleLike(it)}
                          className={`inline-flex items-center gap-1 px-2 h-7 border font-mono text-[11px] transition-colors ${
                            it.liked_by_me
                              ? "border-bear/40 text-bear bg-bear/[0.06]"
                              : "border-white/15 text-neutral-300 hover:border-bear/40 hover:text-bear"
                          }`}
                          data-testid={`like-btn-${it.analysis_id}`}
                          aria-pressed={it.liked_by_me}
                        >
                          <Heart
                            className="h-3 w-3"
                            strokeWidth={2}
                            fill={it.liked_by_me ? "currentColor" : "none"}
                          />
                          <span data-testid={`like-count-${it.analysis_id}`}>{it.share_likes}</span>
                        </button>
                        {it.comments_count > 0 && (
                          <button
                            onClick={() => navigate(`/community/${it.analysis_id}`)}
                            className="inline-flex items-center gap-1 px-2 h-7 border border-white/15 text-neutral-300 hover:border-bull/40 hover:text-bull font-mono text-[11px]"
                            data-testid={`comments-btn-${it.analysis_id}`}
                          >
                            <MessageCircle className="h-3 w-3" strokeWidth={2} />
                            {it.comments_count}
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/community/${it.analysis_id}`)}
                          className="inline-flex items-center gap-1 font-mono text-[11px] text-bull hover:underline"
                          data-testid={`view-btn-${it.analysis_id}`}
                        >
                          View <ArrowRight className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
            {hasMore && (
              <div className="flex justify-center pt-4">
                <Button
                  variant="outline"
                  onClick={() => load(false)}
                  disabled={loadingMore}
                  className="rounded-none border-white/15 bg-transparent hover:bg-bull/5 hover:border-bull/40 hover:text-bull h-10"
                  data-testid="community-load-more"
                >
                  {loadingMore ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    t("community_load_more")
                  )}
                </Button>
              </div>
            )}
          </>
        )}
          </div>

          {/* Sidebar with Leaderboard */}
          <aside className="lg:col-span-4">
            <Leaderboard />
          </aside>
        </div>
      </main>
    </div>
  );
}
